import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './Chatbot.css';

const Chatbot = () => {
    const authInfo = useSelector(state => state.auth.authInfo);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState('');
    const [serviceUnavailable, setServiceUnavailable] = useState(false);
    const messagesEndRef = useRef(null);

    // Generate a unique session ID when component mounts
    useEffect(() => {
        const storedSessionId = localStorage.getItem('chatbot_session_id');

        if (storedSessionId) {
            setSessionId(storedSessionId);
            loadConversationHistory(storedSessionId);
        } else {
            const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            setSessionId(newSessionId);
            localStorage.setItem('chatbot_session_id', newSessionId);
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadConversationHistory = async (sessionId) => {
        try {
            const response = await axios.get(
                `/chatbot/history/${sessionId}`,
                {
                    headers: authInfo?.token ? {
                        'Authorization': `Bearer ${authInfo.token}`
                    } : {}
                }
            );

            if (response.data.success && response.data.messages) {
                const formattedMessages = response.data.messages.map(msg => ({
                    text: msg.content,
                    isUser: msg.role === 'user',
                    timestamp: new Date(msg.timestamp)
                }));
                setMessages(formattedMessages);
            }
        } catch (error) {
            console.error('Error loading conversation history:', error);
        }
    };

    const handleClearConversation = async () => {
        try {
            await axios.delete(`/chatbot/clear/${sessionId}`, {
                headers: authInfo?.token ? {
                    'Authorization': `Bearer ${authInfo.token}`
                } : {}
            });

            setMessages([]);
            setServiceUnavailable(false);

            // Generate new session ID
            const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            setSessionId(newSessionId);
            localStorage.setItem('chatbot_session_id', newSessionId);

        } catch (error) {
            console.error('Error clearing conversation:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!inputMessage.trim() || isLoading) return;

        const userMessage = {
            text: inputMessage,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);
        setServiceUnavailable(false);

        try {
            const currentPage = window.location.pathname;

            const response = await axios.post(
                '/chatbot/message',
                {
                    message: inputMessage,
                    sessionId: sessionId,
                    currentPage: currentPage
                },
                {
                    headers: authInfo?.token ? {
                        'Authorization': `Bearer ${authInfo.token}`
                    } : {}
                }
            );

            if (response.data.success) {
                const botMessage = {
                    text: response.data.message,
                    isUser: false,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMessage]);
            } else {
                throw new Error(response.data.message || 'Failed to get response');
            }

        } catch (error) {
            console.error('Error sending message:', error);

            let errorMessage = 'Sorry, I encountered an error. Please try again.';
            let isServiceError = false;

            // Check if it's a service unavailable error (503)
            if (error.response?.status === 503 || error.response?.data?.serviceUnavailable) {
                errorMessage = error.response?.data?.message || 'The chatbot service is currently unavailable. Please try again later.';
                isServiceError = true;
                setServiceUnavailable(true);
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            const errorMsg = {
                text: errorMessage,
                isUser: false,
                timestamp: new Date(),
                isError: true,
                isServiceError: isServiceError
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            {/* Chatbot Toggle Button */}
            <div
                className={`chatbot-toggle ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="PayEarth AI Assistant"
            >
                {isOpen ? (
                    <i className="bi bi-x-lg"></i>
                ) : (
                    <i className="bi bi-chat-dots-fill"></i>
                )}
            </div>

            {/* Chatbot Window */}
            {isOpen && (
                <div className="chatbot-window">
                    {/* Header */}
                    <div className="chatbot-header">
                        <div className="chatbot-header-info">
                            <i className="bi bi-robot"></i>
                            <div>
                                <h5>PayEarth AI Assistant</h5>
                                <span className={`chatbot-status ${serviceUnavailable ? 'offline' : ''}`}>
                                    {serviceUnavailable ? 'Service Unavailable' : 'Online'}
                                </span>
                            </div>
                        </div>
                        <div className="chatbot-header-actions">
                            <button
                                className="chatbot-action-btn"
                                onClick={handleClearConversation}
                                title="Clear conversation"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                            <button
                                className="chatbot-action-btn"
                                onClick={() => setIsOpen(false)}
                                title="Close chat"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>

                    {/* Service Unavailable Banner */}
                    {serviceUnavailable && (
                        <div className="chatbot-service-banner">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            <span>Chatbot service is currently experiencing issues. Please try again later.</span>
                        </div>
                    )}

                    {/* Messages Container */}
                    <div className="chatbot-messages">
                        {messages.length === 0 && (
                            <div className="chatbot-welcome">
                                <i className="bi bi-chat-heart"></i>
                                <h6>Welcome to PayEarth!</h6>
                                <p>I'm your AI assistant. Ask me anything about products, orders, services, or platform features!</p>
                                <div className="chatbot-quick-questions">
                                    <p className="quick-questions-title">Try asking:</p>
                                    <button
                                        className="quick-question-btn"
                                        onClick={() => setInputMessage("How do I track my order?")}
                                    >
                                        How do I track my order?
                                    </button>
                                    <button
                                        className="quick-question-btn"
                                        onClick={() => setInputMessage("What payment methods do you accept?")}
                                    >
                                        What payment methods do you accept?
                                    </button>
                                    <button
                                        className="quick-question-btn"
                                        onClick={() => setInputMessage("How does the quote system work?")}
                                    >
                                        How does the quote system work?
                                    </button>
                                </div>
                            </div>
                        )}

                        {messages.map((message, index) => (
                            <div
                                key={index}
                                className={`chatbot-message ${message.isUser ? 'user' : 'bot'} ${message.isError ? 'error' : ''} ${message.isServiceError ? 'service-error' : ''}`}
                            >
                                <div className="message-content">
                                    {!message.isUser && (
                                        <div className="message-avatar">
                                            <i className={message.isError ? 'bi bi-exclamation-circle' : 'bi bi-robot'}></i>
                                        </div>
                                    )}
                                    <div className="message-bubble">
                                        <div className="message-text">
                                            <ReactMarkdown>{message.text}</ReactMarkdown>
                                        </div>
                                        <span className="message-time">
                                            {formatTime(message.timestamp)}
                                        </span>
                                    </div>
                                    {message.isUser && (
                                        <div className="message-avatar user">
                                            <i className="bi bi-person-fill"></i>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="chatbot-message bot">
                                <div className="message-content">
                                    <div className="message-avatar">
                                        <i className="bi bi-robot"></i>
                                    </div>
                                    <div className="message-bubble">
                                        <div className="typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Form */}
                    <form className="chatbot-input-form" onSubmit={handleSendMessage}>
                        <input
                            type="text"
                            className="chatbot-input"
                            placeholder={serviceUnavailable ? "Service unavailable..." : "Type your message..."}
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            disabled={isLoading || serviceUnavailable}
                        />
                        <button
                            type="submit"
                            className="chatbot-send-btn"
                            disabled={isLoading || !inputMessage.trim() || serviceUnavailable}
                        >
                            <i className="bi bi-send-fill"></i>
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default Chatbot;
